
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { getMonthlyWaitAverages } from '../../lib/dbFetcher';
import { MONTH_SLUGS, monthFromSlug, slugFromMonth, byMonthMap, isMonthIndexable } from '../../lib/securityWaitByMonth';
const formatHour = (h) => h == null ? null : `${String(h).padStart(2, '0')}:00`;
export async function getStaticPaths({ locales }) {
    const paths = (locales || ['da']).flatMap((locale) => MONTH_SLUGS.map((month) => ({ params: { month }, locale })));
    return { paths, fallback: false };
}
async function getStaticProps({ params, locale }) {
    const month = monthFromSlug(params.month);
    if (month < 1)
        return { notFound: true };
    let months = [];
    try {
        months = await getMonthlyWaitAverages();
    }
    catch (error) {
        console.error('Error building security-wait month page:', error);
        months = Array.from({ length: 12 }, (_, i) => ({
            month: i + 1,
            avgLow: null,
            busiestHour: null,
            busiestAvg: null,
            samples: 0
        }));
    }
    const byMonth = byMonthMap(months);
    const entry = byMonth.get(month) || { avgLow: null, samples: 0 };
    const indexable = isMonthIndexable(entry);
    // Year average across months that have data, for context.
    const known = months.filter((m) => m.avgLow != null).map((m) => m.avgLow);
    const yearAvg = known.length
        ? Math.round((known.reduce((s, v) => s + v, 0) / known.length) * 10) /
            10
        : null;
    return {
        props: { month, months, entry, indexable, yearAvg, locale: locale || 'da' },
        revalidate: 3600
    };
}
function SecurityWaitMonth({ month, months, entry, indexable, yearAvg }) {
    const { t } = useTranslation('security-wait');
    const monthName = t(`month_${month}`);
    const hasData = entry.avgLow != null;
    const busiest = formatHour(entry.busiestHour);
    const answer = hasData
        ? t('answer', { monthName, avg: entry.avgLow, hour: busiest })
        : t('answer_nodata', { monthName });
    let relation = t('relation_average');
    if (hasData && yearAvg != null) {
        if (entry.avgLow > yearAvg + 0.5)
            relation = t('relation_busier');
        else if (entry.avgLow < yearAvg - 0.5)
            relation = t('relation_calmer');
    }
    const faqs = [
        { q: t('faq_q1', { monthName }), a: t('faq_a1', { monthName }) },
        { q: t('faq_q2'), a: t('faq_a2') }
    ];
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a }
        }))
    };
    return (<div className="bg-white p-4 sm:p-6 my-auto rounded max-w-3xl mx-auto text-gray-800">
            <Head>
                <title>{t('meta_title', { monthName })}</title>
                <meta name="description" content={t('meta_description', { monthName })}/>
                {/* Thin-content guard: noindex months we cannot ground in data. */}
                <meta name="robots" content={indexable ? 'all' : 'noindex,follow'}/>
                <meta property="og:title" content={t('meta_title', { monthName })}/>
                <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqJsonLd).replace(/</g, '\\u003c')
        }}/>
            </Head>

            <h1 className="text-2xl sm:text-3xl font-bold">
                {t('page_title', { monthName })}
            </h1>

            {/* Answer-first, data-bearing statement (GEO-extractable). */}
            <p className="mt-4 text-lg">{answer}</p>
            {hasData && yearAvg != null && (<p className="mt-2 text-gray-700">
                    {t('vs_year', { yearAvg, monthName, relation })}
                </p>)}

            <div className="mt-6">
                <Link href="/" className="text-[var(--secondary)] underline">
                    {t('cta_live')}
                </Link>
            </div>

            <h2 className="text-xl font-semibold mt-8">
                {t('year_table_title')}
            </h2>
            <table className="mt-3 w-full text-left border-collapse">
                <thead>
                    <tr className="border-b">
                        <th className="py-2">{t('col_month')}</th>
                        <th className="py-2">{t('col_avg')}</th>
                        <th className="py-2">{t('col_busiest')}</th>
                    </tr>
                </thead>
                <tbody>
                    {months.map((m) => (<tr key={m.month} className={`border-b ${m.month === month ? 'font-semibold' : ''}`}>
                            <td className="py-2">
                                <Link href={`/security-wait/${slugFromMonth(m.month)}`} className="text-[var(--secondary)] underline">
                                    {t(`month_${m.month}`)}
                                </Link>
                            </td>
                            <td className="py-2">
                                {m.avgLow != null ? m.avgLow : '-'}
                            </td>
                            <td className="py-2">
                                {formatHour(m.busiestHour) || '-'}
                            </td>
                        </tr>))}
                </tbody>
            </table>

            <p className="mt-4 text-xs text-gray-500">{t('source_note')}</p>

            <div className="mt-6 flex flex-col gap-2">
                <Link href="/security-wait" className="text-[var(--secondary)] underline">
                    {t('cta_all_months')}
                </Link>
                <Link href="/travel-times" className="text-[var(--secondary)] underline">
                    {t('cta_weekday')}
                </Link>
                <Link href="/best-time-to-arrive" className="text-[var(--secondary)] underline">
                    {t('cta_best_time')}
                </Link>
            </div>

            <h2 className="text-xl font-semibold mt-8">FAQ</h2>
            <dl className="mt-3 divide-y divide-gray-200">
                {faqs.map(({ q, a }, i) => (<div key={i} className="py-3">
                        <dt className="font-semibold">{q}</dt>
                        <dd className="mt-1 text-gray-700">{a}</dd>
                    </div>))}
            </dl>
        </div>);
}
export default SecurityWaitMonth;

    async function __Next_Translate__getStaticProps__1a1723ec73c__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/security-wait/[month]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1723ec73c__ as getStaticProps }
  